import React from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  UNIT_TIME_BEFORE_ARRIVAL,
  UNIT_TIME_AFTER_CHECKOUT,
  scheduleOptions,
  scheduleUnitOptions,
  UNIT_TIME_AFTER_ARRIVAL,
  UNIT_TIME_BEFORE_CHECKOUT,
  UNIT_TIME_AFTER_CONFIRMATION
} from './constants';

import {
  convertToLocalTime,
  convertToUTCTime
} from 'helpers/timeUtils';

import { Grid, TextField, Tooltip, InputLabel, FormControlLabel, Switch } from '@material-ui/core';
import {
  KeyboardDateTimePicker,
  MuiPickersUtilsProvider
} from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import InfoIcon from '@material-ui/icons/Info';
import { SelectInput } from 'components';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles(theme => ({
  root: {},
  infoIcon: {
    marginLeft: '8px',
    marginTop: '14px'
  },
  infoIconForSchedule: {
    marginBottom: '0.5rem'
  },
  alertInfo: {
    marginBottom: '1rem'
  }
}));

const Scheduled = props => {
  const { campaign } = props;
  const {
    thisCampaign,
    onBlur,
    onFocus,
    shouldShowError,
    getErrorMessage,
    handleScheduleRuleChange,
    isCampaignReadOnly
  } = campaign;
  const classes = useStyles();

  const knownRules = [
    UNIT_TIME_BEFORE_ARRIVAL,
    UNIT_TIME_AFTER_CHECKOUT,
    UNIT_TIME_AFTER_ARRIVAL,
    UNIT_TIME_BEFORE_CHECKOUT,
    UNIT_TIME_AFTER_CONFIRMATION
  ];

  // we derive the rule from the property of the scheduleRule object
  // {scheduleRule: {[rule]: [value]}}
  const getRuleInfo = (scheduleRule = {}) => {
    let possibleRules = Object.keys(scheduleRule);

    let rule =
      possibleRules.find(element => knownRules.includes(element)) || '';
    let value = scheduleRule[rule] || null;
    let scheduleUnit = scheduleRule.scheduleUnit;
    let scheduleActionDate = scheduleRule.scheduleActionDate;
    let processOnCheckout = scheduleRule.processOnCheckout;
    let processOnCheckin = scheduleRule.processOnCheckin;

    return [rule, value, scheduleUnit, scheduleActionDate, processOnCheckout, processOnCheckin];
  };

  let [rule, value, scheduleUnit, scheduleActionDate, processOnCheckout, processOnCheckin] = getRuleInfo(
    thisCampaign.scheduleRule
  );

  const isTimeBeforeOptionChosen =
    rule === UNIT_TIME_BEFORE_ARRIVAL ||
    rule === UNIT_TIME_AFTER_CHECKOUT ||
    rule === UNIT_TIME_AFTER_ARRIVAL ||
    rule === UNIT_TIME_BEFORE_CHECKOUT ||
    rule === UNIT_TIME_AFTER_CONFIRMATION;
  const handleRuleChange = e => {
    let eventName = e?.target?.name;
    let value = e?.target?.value;
    let checkedValue = e?.target?.checked;

    if (eventName === 'scheduleRuleName') {
      handleScheduleRuleChange({ ruleName: value });
    }

    if (knownRules.find(rule => eventName?.includes(rule))) {
      let ruleName = eventName.split('.')[1];
      handleScheduleRuleChange({ ruleName: ruleName, value: value, scheduleUnit: scheduleUnit, processOnCheckout: {value: processOnCheckout}, processOnCheckin: {value: processOnCheckin} });
    }

    if (eventName === 'scheduleRule.scheduleUnit') {
      handleScheduleRuleChange({ scheduleUnit: value });
    }

    if (eventName === 'scheduleRule.scheduleActionDate') {
      let updatedValue = value && convertToUTCTime(value);
      handleScheduleRuleChange({
        scheduleActionDate: updatedValue
      });
    }

    if (eventName === 'scheduleRule.scheduleNow') {
      handleScheduleRuleChange({
        scheduleNow: value
      });
    }

    if (eventName === 'scheduleRule.processOnCheckout') {
      handleScheduleRuleChange({
        processOnCheckout: {value: checkedValue}
      });
    }

    if (eventName === 'scheduleRule.processOnCheckin') {
      handleScheduleRuleChange({
        processOnCheckin: {value: checkedValue}
      });
    }
  };

  const getScheduleRuleLabel = () =>
    `Number of ${(
      scheduleUnitOptions.find(option => option.value === scheduleUnit) || {
        label: 'units'
      }
    ).label.toLocaleLowerCase()}`;

  const getSelectInputData = inputName => {
    return {
      fullWidth: true,
      required: true,
      margin: 'dense',
      onFocus: event => onFocus(event),
      onBlur: event => onBlur(event),
      error: shouldShowError(inputName),
      helperText: getErrorMessage(inputName)
    };
  };

  const getScheduleOptions = options => {
    return {
      list: options,
      keyId: 'value',
      valueId: 'value',
      displayValueId: 'label'
    };
  };

  return (
    <>
      {thisCampaign.campaignOccurrenceType === 'ON_DEMAND' &&
        !thisCampaign.recurrence && (
          <MuiPickersUtilsProvider utils={MomentUtils}>
            <Alert severity="info" className={classes.alertInfo}>
              The selected date and time is based on your local time
            </Alert>
            <Grid item md={4} xs={4}>
              <div>
                <KeyboardDateTimePicker
                  margin="dense"
                  inputVariant="outlined"
                  required
                  disabled={isCampaignReadOnly()}
                  id="dateTime-picker"
                  name="scheduleRule.scheduleActionDate"
                  label="Schedule Date&Time"
                  format="MM/DD/YYYY hh:mm a"
                  value={
                    convertToLocalTime((thisCampaign.scheduleRule?.scheduleNow
                      ? null
                      : scheduleActionDate))
                  }
                  onChange={dateTime =>
                    handleRuleChange({
                      target: {
                        name: 'scheduleRule.scheduleActionDate',
                        value: dateTime
                      }
                    })
                  }
                  KeyboardButtonProps={{
                    'aria-label': 'change time'
                  }}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  error={
                    shouldShowError(`scheduleRule.scheduleActionDate`) ||
                    shouldShowError(`scheduleRule`)
                  }
                  helperText={
                    getErrorMessage(`scheduleRule.scheduleActionDate`) ||
                    getErrorMessage(`scheduleRule`)
                  }
                />
                <Tooltip
                  className={classes.infoIcon}
                  title="Please fill this field to schedule for a specific date&time. To schedule immediately, click on SAVE CAMPAIGN & SCHEDULE NOW button.">
                  <InfoIcon />
                </Tooltip>
              </div>
            </Grid>
          </MuiPickersUtilsProvider>
        )}
      {thisCampaign.campaignOccurrenceType === 'ON_DEMAND' &&
        !thisCampaign.recurrence &&
        thisCampaign.scheduleRule?.scheduleNow && (
          <InputLabel style={{ marginTop: '8px' }}>
            This campaign was actioned at the time of last creation/updation.
          </InputLabel>
        )}
      {thisCampaign.campaignOccurrenceType === 'SCHEDULED' && (
        <Grid container spacing={3}>
          <Grid item md={4} xs={4}>
            <SelectInput
              {...getSelectInputData('scheduleRule')}
              label="Schedule campaign"
              name="scheduleRuleName"
              onChange={handleRuleChange}
              disabled={isCampaignReadOnly()}
              value={rule}
              options={getScheduleOptions(scheduleOptions)}
            />
          </Grid>

          {isTimeBeforeOptionChosen && (
            <>
              <Grid item md={3} xs={3}>
                <SelectInput
                  {...getSelectInputData('scheduleRule.scheduleUnit')}
                  label="Units of time"
                  name="scheduleRule.scheduleUnit"
                  onChange={handleRuleChange}
                  disabled={isCampaignReadOnly()}
                  value={scheduleUnit || ''}
                  options={getScheduleOptions(scheduleUnitOptions)}
                />
              </Grid>
              <Grid item md={3} xs={3}>
                <TextField
                  {...getSelectInputData(`scheduleRule.${rule}`)}
                  label={getScheduleRuleLabel()}
                  name={`scheduleRule.${rule}`}
                  onChange={handleRuleChange}
                  disabled={isCampaignReadOnly()}
                  value={value || ''}
                  variant="outlined"></TextField>
              </Grid>
            </>
          )}

          {rule === UNIT_TIME_AFTER_CHECKOUT && (
            <Grid item md={4} xs={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!processOnCheckout}
                    name="scheduleRule.processOnCheckout"
                    onChange={handleRuleChange}
                    disabled={isCampaignReadOnly()}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    color="primary"
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                  />
                }
                label="Process On Checkout"
              />{' '}
              <Tooltip
                className={classes.infoIconForSchedule}
                title="This option only applies to Time after checkout campaigns.">
                <InfoIcon />
              </Tooltip>
            </Grid>
          )}

          {rule === UNIT_TIME_AFTER_ARRIVAL && (
            <Grid item md={4} xs={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!processOnCheckin}
                    name="scheduleRule.processOnCheckin"
                    onChange={handleRuleChange}
                    disabled={isCampaignReadOnly()}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    color="primary"
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                  />
                }
                label="Process On Checkin"
              />{' '}
              <Tooltip
                className={classes.infoIconForSchedule}
                title="This option only applies to Time after arrival campaigns.">
                <InfoIcon />
              </Tooltip>
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
};

export default Scheduled;